import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/axios";

// KYC Queries
// These hit the admin-guarded mirrors under /v1/admin/personal/kyc, not
// /v1/kyc directly - that controller only accepts a personal-user JWT with
// an admin role claim, which is a different secret than the admin token
// this app actually authenticates with, and always 401s here.
const fetchAllKyc = async () => {
  const response = await api.get("/v1/admin/personal/kyc");
  return response.data;
};

export const useAllKyc = () => {
  return useQuery({
    queryKey: ["allKyc"],
    queryFn: fetchAllKyc,
    refetchOnWindowFocus: false,
  });
};

export const useAllUsers = (page, limit, search) => {
  return useQuery({
    queryKey: [`allUsers`, page, limit, search],
    queryFn: async () => {
      try {
        const { data } = await api.get(`/v1/admin/personal/users`, {
          params: {
            page,
            limit,
            ...(search && { search }),
          },
        });
        return data;
      } catch (error) {
        throw error;
      }
    },
  });
};

/**
 * Utility to fetch all users across all pages.
 * Useful for exporting or full dataset analysis.
 */
export const fetchAllUsers = async (search = "") => {
  let allData = [];
  let currentPage = 1;
  let totalPages = 1;

  try {
    do {
      const { data } = await api.get(`/v1/admin/personal/users`, {
        params: {
          page: currentPage,
          limit: 100, // Max limit
          ...(search && { search }),
        },
      });

      if (data?.data) {
        allData = [...allData, ...data.data];
      }

      totalPages = data?.meta?.totalPages || 1;
      currentPage++;
    } while (currentPage <= totalPages);

    return allData;
  } catch (error) {
    console.error("Error fetching all users:", error);
    throw error;
  }
};

const fetchCountries = async () => {
  const response = await api.get(`/v1/business/countries/all`);
  return response.data;
};

export const useCountries = () => {
  return useQuery({
    queryKey: ["countries"],
    queryFn: fetchCountries,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

const fetchCurrencies = async () => {
  const response = await api.get(`/v1/currency`);
  return response.data;
};

// One row per (currency, country) pair - dedupe by currencyCode since
// campaign task configs only care about the currency itself, not which
// country it's tied to. Each row also carries canConvertTo, which lets the
// Currency Convert task offer only real, DB-configured conversion pairs.
export const useCurrencies = () => {
  return useQuery({
    queryKey: ["currencies"],
    queryFn: fetchCurrencies,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    select: (res) => {
      const seen = new Map();
      for (const currency of res.data || []) {
        if (!seen.has(currency.currencyCode)) seen.set(currency.currencyCode, currency);
      }
      return [...seen.values()];
    },
  });
};

const fetchKycById = async (id) => {
  const response = await api.get(`/v1/admin/personal/kyc/${id}`);
  return response.data;
};

export const useKycById = (id) => {
  return useQuery({
    queryKey: ["kyc", id],
    queryFn: () => fetchKycById(id),
    refetchOnWindowFocus: false,
    enabled: !!id, // Only run the query if an ID is provided
  });
};

const fetchUserById = async (uuid) => {
  const response = await api.get(`/v1/admin/personal/user/${uuid}`);
  return response.data;
};

export const useUserById = (uuid) => {
  return useQuery({
    queryKey: ["user", uuid], // Fixed queryKey - was incorrectly using 'kyc'
    queryFn: () => fetchUserById(uuid),
    refetchOnWindowFocus: false,
    enabled: !!uuid, // Only run the query if an ID is provided
  });
};

export const useDisableUserById = () => {
  const queryClient = useQueryClient(); // Added queryClient

  return useMutation({
    mutationFn: async (uuid) => {
      const response = await api.patch(`/v1/admin/personal/user/${uuid}`, {
        isActive: false,
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Added variables parameter
      console.log("User disabled successfully:", data);
      queryClient.invalidateQueries(["user", variables]); // Added cache invalidation
      queryClient.invalidateQueries(["allUsers"]);
    },
    onError: (error) => {
      console.error("Error disabling user:", error);
    },
  });
};

export const useDeleteUserById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (uuid) => {
      const response = await api.delete(`/v1/admin/personal/user/${uuid}`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["user", variables]);
      queryClient.invalidateQueries(["allUsers"]);
    },
    onError: (error) => {
      console.error("Error deleting user:", error);
    },
  });
};

export const useRestoreUserById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (uuid) => {
      const response = await api.patch(`/v1/admin/personal/user/${uuid}/restore`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["user", variables]);
      queryClient.invalidateQueries(["allUsers"]);
    },
    onError: (error) => {
      console.error("Error restoring user:", error);
    },
  });
};

export const useRequestResetUserPin = () => {
  return useMutation({
    mutationFn: async (uuid) => {
      const response = await api.post(
        `/v1/admin/personal/user/${uuid}/reset-pin/request`
      );
      return response.data;
    },
  });
};

export const useConfirmResetUserPin = () => {
  return useMutation({
    mutationFn: async ({ uuid, code }) => {
      const response = await api.post(
        `/v1/admin/personal/user/${uuid}/reset-pin/confirm`,
        { code }
      );
      return response.data;
    },
  });
};

export const useEnableUserById = () => {
  const queryClient = useQueryClient(); // Added queryClient

  return useMutation({
    mutationFn: async (uuid) => {
      const response = await api.patch(`/v1/admin/personal/user/${uuid}`, {
        isActive: true,
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Added variables parameter and fixed log message
      console.log("User enabled successfully:", data); // Fixed message from 'disabled' to 'enabled'
      queryClient.invalidateQueries(["user", variables]); // Added cache invalidation
      queryClient.invalidateQueries(["allUsers"]);
    },
    onError: (error) => {
      console.error("Error enabling user:", error); // Fixed error message
    },
  });
};

export const useUpdateKycById = () => {
  const queryClient = useQueryClient(); // Added queryClient

  return useMutation({
    mutationFn: async ({ data }) => {
      // Fixed parameter destructuring
      const response = await api.patch(`/v1/admin/personal/kyc/decline`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Added variables parameter
      console.log("KYC updated successfully:", data);
      queryClient.invalidateQueries(["kyc"]); // Added cache invalidation
      queryClient.invalidateQueries(["allKyc"]);
      // Also invalidate the linked user's cache - approve/decline both
      // touch the User record too (hasCompletedKYC), and the response
      // carries the KYC's nested user relation.
      const uuid = data?.data?.user?.uuid;
      if (uuid) queryClient.invalidateQueries(["user", uuid]);
    },
    onError: (error) => {
      console.error("Error updating KYC:", error);
    },
  });
};

export const useApproveKycById = () => {
  const queryClient = useQueryClient(); // Added queryClient

  return useMutation({
    mutationFn: async ({ id }) => {
      // Fixed parameter destructuring
      const response = await api.patch(`/v1/admin/personal/kyc/approve/${id}`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Added variables parameter
      console.log("KYC updated successfully:", data);
      queryClient.invalidateQueries(["kyc", variables.id]); // Added cache invalidation
      queryClient.invalidateQueries(["allKyc"]);
      const uuid = data?.data?.user?.uuid;
      if (uuid) queryClient.invalidateQueries(["user", uuid]);
    },
    onError: (error) => {
      console.error("Error updating KYC:", error);
    },
  });
};

export const useApproveBusinessKycById = () => {
  const queryClient = useQueryClient(); // Added queryClient

  return useMutation({
    mutationFn: async (businessuuid) => {
      const response = await api.post(
        `/v1/admin/business/kyc/${businessuuid}/approve`
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Added variables parameter
      console.log("KYC updated successfully:", data);
      queryClient.invalidateQueries(["allBusiness"]); // Added cache invalidation
    },
    onError: (error) => {
      console.error("Error updating KYC:", error);
    },
  });
};

export const useRejectBusinessKycById = () => {
  const queryClient = useQueryClient(); // Added queryClient

  return useMutation({
    mutationFn: async (businessuuid) => {
      const response = await api.post(
        `/v1/admin/business/kyc/${businessuuid}/reject`
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Added variables parameter
      console.log("KYC updated successfully:", data);
      queryClient.invalidateQueries(["allBusiness"]); // Added cache invalidation
    },
    onError: (error) => {
      console.error("Error updating KYC:", error);
    },
  });
};

const fetchAllBusiness = async ({ page = 1, limit = 10, status, search }) => {
  const response = await api.get(`/v1/business/admin/business/kyc`, {
    params: {
      page,
      limit,
      status,
      search,
    },
  });
  return response.data;
};

export const useAllBusiness = (page = 1, limit = 10, status, search) => {
  return useQuery({
    queryKey: ["allBusiness", page, limit, status, search],
    queryFn: () => fetchAllBusiness({ page, limit, status, search }),
    refetchOnWindowFocus: false,
  });
};

export const updateUser = async ({ uuid, payload }) => {
  const { data } = await api.patch(
    `/v1/admin/personal/user/${uuid}`,
    payload
  );
  return data;
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUser,

    onSuccess: (data, variables) => {
      console.log("User updated successfully:", data);

      // Optional: refetch user details
      queryClient.invalidateQueries({
        queryKey: ["user", variables.uuid],
      });
      queryClient.invalidateQueries(["allUsers"]);
    },

    onError: (error) => {
      console.error("Update failed:", error);
    },
  });
};

export const retryKyc = async ({ uuid }) => {
  const { data } = await api.post(`/v1/admin/personal/user/${uuid}/retry-kyc`);
  return data;
};

export const useRetryKyc = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: retryKyc,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user", variables.uuid] });
    },
  });
};

export const uploadKycDocument = async ({ uuid, file }) => {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post(
    `/v1/admin/personal/user/${uuid}/kyc-document`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
};

export const useUploadKycDocument = () => {
  return useMutation({
    mutationFn: uploadKycDocument,
  });
};

// Compliance hooks
export const useApproveCompliance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId }) => {
      // Added mutationFn property
      const response = await api.patch(`/v1/admin/personal/kyc/approve/${userId}`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["user", variables.userId]);
      queryClient.invalidateQueries(["allUsers"]); // Fixed query key
    },
  });
};

export const useDeclineCompliance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      // Get data from mutateAsync call
      console.log(data);
      const response = await api.patch(`v1/kyc/decline`, data); // Don't wrap in another object
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["user", variables.userId]);
      queryClient.invalidateQueries(["allUsers"]);
    },
  });
};
